import { useState, useEffect, useCallback } from "react";
import { ChevronLeft, Heart, Share2, ChevronRight, ShoppingCart, MapPin, Clock, Check, MessageCircle, ArrowLeft, ZoomIn, ZoomOut, Tag } from "lucide-react";
import type { Ad } from "@/app/components/ad-card";
import { Button } from "@/app/components/button";
import { formatCurrency, formatDate } from "@/config/locale";
import { shareAdListing } from "@/app/utils/share-ad-listing";
import { AdImage } from "@/app/components/common/ad-image";
import { formatAdCategoryDisplayLine, getAdImages } from "@/app/utils/ad-utils";
import { BottomNav } from "./bottom-nav";
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from "@/app/components/ui/dialog";
import { ListingAttributesDisplay } from "@/app/components/listing-attributes-display";
import { useAccountNavEnabled } from "@/app/context/account-nav-context";

interface AdDetailPageProps {
  ad: Ad;
  onBack: () => void;
  isFavorite?: boolean;
  onToggleFavorite?: (id: string) => void;
  isInCart?: boolean;
  onAddToCart?: (id: string) => void;
  onRemoveFromCart?: (id: string) => void;
  activeTab: "home" | "favorites";
  onTabChange: (tab: "home" | "favorites") => void;
  favoritesCount: number;
  onMenuClick: () => void;
  onPostAdClick: () => void;
  postAdBlockedByUnverifiedEmail?: boolean;
  onChatWithSeller?: (adId: string, adTitle: string, sellerId?: string) => void;
  /** (sellerId, sellerName) for navigation to seller profile. */
  onSellerClick?: (sellerId: string, sellerName: string) => void;
  onMakeOffer?: () => void;
  /** When true, viewer is the seller; hide Add to cart, Send offer, Chat. */
  isSeller?: boolean;
  /** When true, current user already has a pending offer for this ad; disable "Enviar Oferta". */
  hasPendingOffer?: boolean;
  /** Unused: buyer actions are gated by account-nav context (profile-complete hub). */
  isLoggedIn?: boolean;
}

export function AdDetailPage({ 
  ad, 
  onBack, 
  isFavorite, 
  onToggleFavorite, 
  isInCart, 
  onAddToCart,
  onRemoveFromCart,
  activeTab,
  onTabChange,
  favoritesCount,
  onMenuClick,
  onPostAdClick,
  postAdBlockedByUnverifiedEmail = false,
  onChatWithSeller,
  onSellerClick,
  onMakeOffer,
  isSeller = false,
  hasPendingOffer = false,
  isLoggedIn: _legacyIsLoggedIn = true,
}: AdDetailPageProps) {
  const accountNav = useAccountNavEnabled();
  const normalizedStatus = (ad.status ?? "active").toLowerCase().trim();
  const isListingActive = normalizedStatus === "active";
  const images = getAdImages(ad).length > 0 ? getAdImages(ad) : [ad.imageUrl || ad.image || ""].filter(Boolean);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [zoomModalOpen, setZoomModalOpen] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(1);

  const ZOOM_MIN = 0.5;
  const ZOOM_MAX = 3;
  const ZOOM_STEP = 0.25;

  const zoomIn = useCallback(() => {
    setZoomLevel((z) => Math.min(ZOOM_MAX, z + ZOOM_STEP));
  }, []);
  const zoomOut = useCallback(() => {
    setZoomLevel((z) => Math.max(ZOOM_MIN, z - ZOOM_STEP));
  }, []);
  const zoomReset = useCallback(() => setZoomLevel(1), []);

  // Reset image index when ad changes
  useEffect(() => {
    setCurrentImageIndex(0);
  }, [ad.id]);

  useEffect(() => {
    if (ad.indexingAllowed !== false) {
      return;
    }
    const prevTitle = document.title;
    document.title = "akihub";
    let meta = document.querySelector('meta[name="robots"][data-ezyhub-review="1"]') as HTMLMetaElement | null;
    if (!meta) {
      meta = document.createElement("meta");
      meta.setAttribute("name", "robots");
      meta.setAttribute("data-ezyhub-review", "1");
      document.head.appendChild(meta);
    }
    meta.setAttribute("content", "noindex, nofollow");
    return () => {
      document.title = prevTitle;
      if (meta && meta.parentNode) {
        meta.parentNode.removeChild(meta);
      }
    };
  }, [ad.id, ad.indexingAllowed]);

  // Reset zoom when opening modal
  useEffect(() => {
    if (zoomModalOpen) setZoomLevel(1);
  }, [zoomModalOpen]);

  const handleToggleCart = () => {
    if (isInCart && onRemoveFromCart) {
      onRemoveFromCart(ad.id);
    } else if (!isInCart && onAddToCart) {
      onAddToCart(ad.id);
    }
  };

  const goToNextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % images.length);
  };

  const goToPreviousImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  const publishedLabel = (() => {
    const raw = ad.createdAt?.trim();
    if (!raw) return null;
    const d = new Date(raw);
    if (Number.isNaN(d.getTime())) return null;
    return formatDate(d, "long");
  })();

  const categoryDisplayLine = formatAdCategoryDisplayLine(ad);

  const handleShareAd = () => void shareAdListing({ adId: ad.id, title: ad.title, price: ad.price });

  return (
    <div className="min-h-screen bg-background pb-[calc(10rem+env(safe-area-inset-bottom,0px))] lg:pb-24">
      {/* Header */}
      <header className="bg-primary border-b border-primary sticky top-0 z-40 shadow-sm">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center gap-4 px-4 py-4">
            <button
              onClick={onBack}
              className="p-2 hover:bg-white/20 rounded-lg transition-colors -ml-2"
            >
              <ArrowLeft className="w-6 h-6 text-white" />
            </button>
            <h1 className="text-lg font-medium flex-1 text-white">Detalhes do Anúncio</h1>
            <div className="flex gap-2">
              {accountNav && onToggleFavorite && (
                <button
                  onClick={() => onToggleFavorite(ad.id)}
                  className="p-3 bg-white/20 backdrop-blur-sm hover:bg-white/30 rounded-lg transition-colors"
                >
                  <Heart
                    className={`w-5 h-5 transition-colors ${
                      isFavorite ? "fill-white text-white" : "text-white"
                    }`}
                  />
                </button>
              )}
              <button
                type="button"
                onClick={() => void handleShareAd()}
                className="p-3 bg-white/20 backdrop-blur-sm hover:bg-white/30 rounded-lg transition-colors"
                aria-label="Compartilhar anúncio"
              >
                <Share2 className="w-5 h-5 text-white" />
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Content */}
      <div className="max-w-7xl mx-auto">
        {/* Image Gallery: narrower centered, contain so image fits (Option D + Option 1) */}
        <div
          className="relative w-full max-w-lg mx-auto aspect-[4/3] max-h-[50vh] bg-secondary cursor-zoom-in"
          role="button"
          tabIndex={0}
          onClick={() => setZoomModalOpen(true)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              setZoomModalOpen(true);
            }
          }}
          aria-label="Ampliar imagem"
        >
          <AdImage
            src={images[currentImageIndex]}
            alt={`${ad.title} - Imagem ${currentImageIndex + 1}`}
            className="w-full h-full object-contain pointer-events-none"
          />
          {/* Magnify hint */}
          <div className="absolute bottom-4 left-4 p-2 rounded-lg bg-black/50 text-white/90" aria-hidden>
            <ZoomIn className="w-5 h-5" />
          </div>

          {/* Navigation Arrows */}
          {images.length > 1 && (
            <>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  goToPreviousImage();
                }}
                className="absolute left-4 top-1/2 -translate-y-1/2 p-3 bg-black/50 hover:bg-black/70 text-white rounded-full transition-all hover:scale-110"
                aria-label="Imagem anterior"
              >
                <ChevronLeft className="w-6 h-6" />
              </button>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  goToNextImage();
                }}
                className="absolute right-4 top-1/2 -translate-y-1/2 p-3 bg-black/50 hover:bg-black/70 text-white rounded-full transition-all hover:scale-110"
                aria-label="Próxima imagem"
              >
                <ChevronRight className="w-6 h-6" />
              </button>
              
              {/* Image Counter */}
              <div className="absolute bottom-4 right-4 px-3 py-1.5 bg-black/70 text-white text-sm rounded-full">
                {currentImageIndex + 1} / {images.length}
              </div>
            </>
          )}
        </div>

        {/* Thumbnail Strip (centered below gallery) */}
        {images.length > 1 && (
          <div className="px-4 py-3 overflow-x-auto flex justify-center">
            <div className="flex gap-2">
              {images.map((img, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentImageIndex(index)}
                  className={`shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 transition-all ${
                    index === currentImageIndex
                      ? "border-primary scale-110"
                      : "border-border hover:border-primary/50"
                  }`}
                >
                  <AdImage
                    src={img}
                    alt={`Miniatura ${index + 1}`}
                    className="w-full h-full object-cover"
                  />
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Zoom modal: fullscreen lightbox with zoom in/out */}
        <Dialog open={zoomModalOpen} onOpenChange={setZoomModalOpen}>
          <DialogTitle className="sr-only">
            Ampliar imagem {currentImageIndex + 1} de {images.length}
          </DialogTitle>
          <DialogContent
            className="fixed inset-0 z-50 w-screen h-screen max-w-none border-0 p-0 gap-0 bg-black/95 rounded-none flex flex-col items-center justify-center translate-x-0 translate-y-0 top-0 left-0 right-0 bottom-0 pb-[env(safe-area-inset-bottom,0px)] [&>button:last-child]:text-white [&>button:last-child]:z-20 [&>button:last-child]:top-[max(0.75rem,env(safe-area-inset-top,0px))] [&>button:last-child]:right-[max(0.75rem,env(safe-area-inset-right,0px))] [&>button:last-child]:opacity-90 [&>button:last-child]:hover:opacity-100"
          >
            <div className="flex-1 w-full min-h-0 overflow-auto flex items-center justify-center p-4">
              <div
                className="flex items-center justify-center transition-transform duration-200 origin-center"
                style={{ transform: `scale(${zoomLevel})` }}
              >
                <AdImage
                  src={images[currentImageIndex]}
                  alt={`${ad.title} - Imagem ${currentImageIndex + 1}`}
                  className="max-w-full max-h-[85vh] w-auto h-auto object-contain"
                />
              </div>
            </div>
            {/* Prev/next in modal when multiple images */}
            {images.length > 1 && (
              <>
                <button
                  type="button"
                  onClick={goToPreviousImage}
                  className="absolute top-1/2 -translate-y-1/2 p-3 bg-white/20 hover:bg-white/30 text-white rounded-full transition-colors left-[max(1rem,env(safe-area-inset-left,0px))]"
                  aria-label="Imagem anterior"
                >
                  <ChevronLeft className="w-8 h-8" />
                </button>
                <button
                  type="button"
                  onClick={goToNextImage}
                  className="absolute top-1/2 -translate-y-1/2 p-3 bg-white/20 hover:bg-white/30 text-white rounded-full transition-colors right-[max(1rem,env(safe-area-inset-right,0px))]"
                  aria-label="Próxima imagem"
                >
                  <ChevronRight className="w-8 h-8" />
                </button>
                <div className="absolute left-1/2 -translate-x-1/2 px-3 py-1.5 bg-white/20 text-white text-sm rounded-full bottom-[max(1.5rem,env(safe-area-inset-bottom,0px))]">
                  {currentImageIndex + 1} / {images.length}
                </div>
              </>
            )}
            {/* Zoom controls */}
            <div className="absolute flex items-center gap-2 p-2 rounded-xl bg-white/10 backdrop-blur-sm bottom-[max(1.5rem,env(safe-area-inset-bottom,0px))] right-[max(1rem,env(safe-area-inset-right,0px))]">
              <button
                type="button"
                onClick={zoomOut}
                disabled={zoomLevel <= ZOOM_MIN}
                className="p-2 text-white rounded-lg hover:bg-white/20 disabled:opacity-40 disabled:pointer-events-none transition-colors"
                aria-label="Reduzir zoom"
              >
                <ZoomOut className="w-5 h-5" />
              </button>
              <button
                type="button"
                onClick={zoomReset}
                className="min-w-[3rem] px-2 py-1 text-white text-sm font-medium rounded-lg hover:bg-white/20 transition-colors"
                aria-label="Zoom padrão"
              >
                {Math.round(zoomLevel * 100)}%
              </button>
              <button
                type="button"
                onClick={zoomIn}
                disabled={zoomLevel >= ZOOM_MAX}
                className="p-2 text-white rounded-lg hover:bg-white/20 disabled:opacity-40 disabled:pointer-events-none transition-colors"
                aria-label="Aumentar zoom"
              >
                <ZoomIn className="w-5 h-5" />
              </button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Product Info */}
        <div className="p-4 sm:p-6 space-y-6">
          {/* Title and Price */}
          <div>
            <h2 className="text-2xl sm:text-3xl mb-4 break-words">{ad.title}</h2>
            <p className="text-3xl sm:text-4xl text-primary font-medium">
              {formatCurrency(ad.price)}
            </p>
          </div>

          {/* Metadata: one row each (location, published date, category) */}
          <div className="pt-4 mt-2 border-t border-border">
            <div className="flex flex-col gap-3 text-sm text-muted-foreground">
              <div className="flex min-w-0 items-start gap-2">
                <MapPin className="h-4 w-4 shrink-0 mt-0.5" aria-hidden />
                <span className="min-w-0 break-words">{ad.location}</span>
              </div>
              {publishedLabel ? (
                <div className="flex min-w-0 items-start gap-2">
                  <Clock className="h-4 w-4 shrink-0 mt-0.5" aria-hidden />
                  <span className="min-w-0 break-words">Publicado {publishedLabel}</span>
                </div>
              ) : null}
              {categoryDisplayLine ? (
                <div className="flex min-w-0 items-center gap-2">
                  <Tag className="h-4 w-4 shrink-0" aria-hidden />
                  <span
                    className="min-w-0 whitespace-nowrap overflow-x-auto max-w-full leading-snug [scrollbar-width:thin]"
                    title={categoryDisplayLine}
                  >
                    {categoryDisplayLine}
                  </span>
                </div>
              ) : null}
            </div>
          </div>

          {/* Description */}
          <div className="border-t border-border pt-6">
            <h3 className="mb-3 text-lg font-medium">Descrição</h3>
            <p className="text-muted-foreground leading-relaxed whitespace-pre-line">
              {ad.description}
            </p>
          </div>

          <ListingAttributesDisplay
            categoryId={ad.category_id}
            listingAttributes={ad.listing_attributes}
          />

          {/* Seller Info */}
          <div className="border-t border-border pt-6">
            <h3 className="mb-3 text-lg font-medium">Vendedor</h3>
            <button
              onClick={() => onSellerClick?.(ad.seller_id ?? "", ad.seller || "Vendedor")}
              className="w-full flex items-center gap-4 p-4 bg-secondary/50 rounded-xl hover:bg-secondary/70 transition-colors cursor-pointer text-left"
            >
              <div className="w-14 h-14 bg-primary rounded-full flex items-center justify-center text-primary-foreground text-xl font-medium">
                {(ad.seller || "Vendedor")
                  .split(" ")
                  .map((n) => n[0])
                  .join("")
                  .toUpperCase()
                  .slice(0, 2)}
              </div>
              <div className="flex-1">
                <p className="font-medium">{ad.seller || "Vendedor"}</p>
                {ad.sellerMemberSinceYear ? (
                  <p className="text-sm text-muted-foreground">Membro desde {ad.sellerMemberSinceYear}</p>
                ) : null}
              </div>
              <ArrowLeft className="w-5 h-5 text-muted-foreground rotate-180" />
            </button>
          </div>

          {!isSeller && accountNav && !isListingActive ? (
            <div className="rounded-xl border border-border bg-muted/40 p-4 text-center text-muted-foreground">
              {normalizedStatus === "sold" ? (
                <p className="font-medium text-foreground">Este anúncio foi vendido e não aceita compra, ofertas ou chat.</p>
              ) : (
                <p className="font-medium text-foreground">Este anúncio não está disponível no momento.</p>
              )}
            </div>
          ) : null}

          {/* Action Buttons (hidden when viewer is the seller, not logged in, or listing not active) */}
          {!isSeller && accountNav && isListingActive && (
            <div className="flex flex-col gap-3 pt-4">
              {(onAddToCart || onRemoveFromCart) && (
                <Button 
                  onClick={handleToggleCart}
                  className={`w-full py-6 text-lg flex items-center justify-center gap-2 transition-all duration-300 ${
                    isInCart 
                      ? 'border-2' 
                      : 'shadow-lg hover:shadow-xl hover:scale-[1.02]'
                  }`}
                  style={
                    isInCart
                      ? {
                          backgroundColor: 'transparent',
                          borderColor: 'var(--orange)',
                          color: 'var(--orange)',
                        }
                      : {
                          backgroundColor: 'var(--orange)',
                          color: 'var(--orange-foreground)',
                        }
                  }
                  onMouseEnter={(e) => {
                    if (!isInCart) {
                      e.currentTarget.style.backgroundColor = 'var(--orange-hover)';
                    } else {
                      e.currentTarget.style.backgroundColor = 'rgba(249, 115, 22, 0.1)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isInCart) {
                      e.currentTarget.style.backgroundColor = 'var(--orange)';
                    } else {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }
                  }}
                  variant={isInCart ? "outline" : "default"}
                >
                  {isInCart ? (
                    <>
                      <Check className="w-5 h-5" />
                      No Carrinho
                    </>
                  ) : (
                    <>
                      <ShoppingCart className="w-5 h-5" />
                      Adicionar ao Carrinho
                    </>
                  )}
                </Button>
              )}
              <div className="grid grid-cols-2 gap-3">
                {ad.acceptsOffers !== false && (
                  <Button 
                    className={`w-full py-6 text-lg transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-[1.02] ${
                      hasPendingOffer
                        ? "bg-muted text-muted-foreground cursor-not-allowed hover:scale-100"
                        : ""
                    }`}
                    style={hasPendingOffer ? undefined : {
                      backgroundColor: 'var(--orange)',
                      color: 'var(--orange-foreground)',
                    }}
                    onMouseEnter={(e) => {
                      if (!hasPendingOffer) e.currentTarget.style.backgroundColor = 'var(--orange-hover)';
                    }}
                    onMouseLeave={(e) => {
                      if (!hasPendingOffer) e.currentTarget.style.backgroundColor = 'var(--orange)';
                    }}
                    onClick={onMakeOffer}
                    disabled={hasPendingOffer}
                  >
                    {hasPendingOffer ? "Oferta já enviada" : "Enviar Oferta"}
                  </Button>
                )}
                {onChatWithSeller && (
                  <Button 
                    variant="outline" 
                    className={`w-full py-6 text-lg flex items-center justify-center gap-2 transition-all duration-300 hover:scale-[1.02] ${ad.acceptsOffers === false ? 'col-span-2' : ''}`}
                    onClick={() => onChatWithSeller(ad.id, ad.title, ad.seller_id)}
                  >
                    <MessageCircle className="w-5 h-5" />
                    Chat
                  </Button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Bottom Navigation */}
      <BottomNav
        activeTab={activeTab}
        onTabChange={(tab) => {
          if (tab === "home" || tab === "favorites") onTabChange(tab);
        }}
        favoritesCount={favoritesCount}
        onMenuClick={onMenuClick}
        onPostAdClick={onPostAdClick}
        postAdBlockedByUnverifiedEmail={postAdBlockedByUnverifiedEmail}
      />
    </div>
  );
}